import { AbstractControl, FormGroup, ValidationErrors, ValidatorFn, Validators } from '@angular/forms';
import { MediaType } from '@core/attachment/media-type.typings';
import { isEmpty } from '@core/utils';
import { parsePhoneNumberWithError, ParseError } from 'libphonenumber-js';

export type Predicate = 'eq' | 'ne' | 'lt' | 'le' | 'ge' | 'gt';

type CompareError = {
  actualPredicate: Predicate;
  requiredPredicate: Predicate;
  field1?: string;
  field2?: string;
};

type CompareFieldError = {
  compare: CompareError;
};

type CompareFormError = {
  compare: {
    first: CompareError;
    second: CompareError;
  };
};

type CompareToSumTargetFieldError = {
  compareToSumTarget: CompareError;
};

type CompareToSumSummandFieldError = {
  compareToSumSummand: CompareError;
};

type CompareToSumFormError = {
  compareToSum: {
    target: CompareError;
    firstSummand: CompareError;
    secondSummand: CompareError;
  };
};

export type PhoneError = {
  phone: 'INVALID_COUNTRY' | 'NOT_A_NUMBER' | 'OTHER' | 'TOO_SHORT' | 'TOO_LONG' | 'MISSING_PLUS';
};

const stripError = (control: AbstractControl, key: string): ValidationErrors | null => {
  const err = control.errors;

  if (!err) {
    return null;
  }

  delete err[key];

  if (Object.keys(err).length) {
    return err;
  }

  return null;
};

// Max Values
export const emailMaxLength = 254;
export const longTextMaxLength = 8000;
export const mediumTextMaxLength = 150;
export const shortTextMaxLength = 30;
export const vatIdNumberMaxLength = 20;

export class WaiValidators {
  public static compare(
    first: string,
    predicate: Predicate,
    second: string,
    type: 'number' | 'string' | 'date' = 'number',
  ): ValidatorFn {
    const ERROR_KEY = 'compare';

    const getRefs = (group: FormGroup) => {
      return {
        field1: group.get(first),
        field2: group.get(second),
        value1: group.get(first).value,
        value2: group.get(second).value,
      };
    };

    return (group: FormGroup): CompareFormError | null => {
      const { field1, field2, value1, value2 } = getRefs(group);

      field1.setErrors(stripError(field1, ERROR_KEY));
      field2.setErrors(stripError(field2, ERROR_KEY));

      // no error for empty fields
      if (!value1 || !value2) {
        return null;
      }

      let val1: string | number;
      let val2: string | number;

      switch (type) {
        case 'number':
          val1 = parseFloat(value1);
          val2 = parseFloat(value2);
          break;
        case 'date':
          val1 = new Date(value1).getTime();
          val2 = new Date(value2).getTime();
          break;
        default:
          val1 = value1;
          val2 = value2;
      }

      const predicates = new Map<Predicate, { error: CompareError; passes: boolean; reverse: Predicate }>([
        [
          'eq',
          {
            passes: val1 === val2,
            reverse: 'eq',
            error: { requiredPredicate: 'eq', actualPredicate: 'ne', field2: second },
          },
        ],
        [
          'ne',
          {
            passes: val1 !== val2,
            reverse: 'ne',
            error: { requiredPredicate: 'ne', actualPredicate: 'eq', field2: second },
          },
        ],
        [
          'lt',
          {
            passes: val1 < val2,
            reverse: 'gt',
            error: { requiredPredicate: 'lt', actualPredicate: 'ge', field2: second },
          },
        ],
        [
          'le',
          {
            passes: val1 <= val2,
            reverse: 'ge',
            error: { requiredPredicate: 'le', actualPredicate: 'gt', field2: second },
          },
        ],
        [
          'ge',
          {
            passes: val1 >= val2,
            reverse: 'le',
            error: { requiredPredicate: 'ge', actualPredicate: 'lt', field2: second },
          },
        ],
        [
          'gt',
          {
            passes: val1 > val2,
            reverse: 'lt',
            error: { requiredPredicate: 'gt', actualPredicate: 'le', field2: second },
          },
        ],
      ]);

      if (predicates.has(predicate) && predicates.get(predicate).passes) {
        return null;
      }

      const result1 = predicates.get(predicate);
      const result2 = predicates.get(result1.reverse);

      // field1's error references field2, field2's error references field1
      const error1 = { ...result1.error, field2: second };
      const error2 = { ...result2.error, field2: first };

      const err1: CompareFieldError = { [ERROR_KEY]: error1 };
      const err2: CompareFieldError = { [ERROR_KEY]: error2 };

      field1.setErrors(err1);
      field2.setErrors(err2);

      field1.markAsTouched();
      field2.markAsTouched();

      return {
        [ERROR_KEY]: {
          first: error1,
          second: error2,
        },
      };
    };
  }

  public static compareToSum(
    target: string,
    predicate: Predicate,
    firstSummand: string,
    secondSummand: string,
  ): ValidatorFn {
    const ERROR_KEY_TARGET = 'compareToSumTarget';
    const ERROR_KEY_SUMMAND = 'compareToSumSummand';

    const getRefs = (group: FormGroup) => {
      return {
        targetField: group.get(target),
        summandField1: group.get(firstSummand),
        summandField2: group.get(secondSummand),
        targetValue: group.get(target).value,
        summandValue1: group.get(firstSummand).value,
        summandValue2: group.get(secondSummand).value,
      };
    };

    return (group: FormGroup): CompareToSumFormError | null => {
      const { targetField, summandField1, summandField2, targetValue, summandValue1, summandValue2 } = getRefs(group);

      targetField.setErrors(stripError(targetField, ERROR_KEY_TARGET));
      summandField1.setErrors(stripError(summandField1, ERROR_KEY_SUMMAND));
      summandField2.setErrors(stripError(summandField2, ERROR_KEY_SUMMAND));

      // no error for empty fields
      if (!targetValue || (!summandValue1 && !summandValue2)) {
        return null;
      }

      let targetVal = parseFloat(targetValue);
      let sum = (parseFloat(summandValue1) || 0) + (parseFloat(summandValue2) || 0);

      const predicates = new Map<Predicate, { error: CompareError; passes: boolean; reverse: Predicate }>([
        [
          'eq',
          {
            passes: targetVal === sum,
            reverse: 'eq',
            error: { requiredPredicate: 'eq', actualPredicate: 'ne', field1: firstSummand, field2: secondSummand },
          },
        ],
        [
          'ne',
          {
            passes: targetVal !== sum,
            reverse: 'ne',
            error: { requiredPredicate: 'ne', actualPredicate: 'eq', field1: firstSummand, field2: secondSummand },
          },
        ],
        [
          'lt',
          {
            passes: targetVal < sum,
            reverse: 'gt',
            error: { requiredPredicate: 'lt', actualPredicate: 'ge', field1: firstSummand, field2: secondSummand },
          },
        ],
        [
          'le',
          {
            passes: targetVal <= sum,
            reverse: 'ge',
            error: { requiredPredicate: 'le', actualPredicate: 'gt', field1: firstSummand, field2: secondSummand },
          },
        ],
        [
          'ge',
          {
            passes: targetVal >= sum,
            reverse: 'le',
            error: { requiredPredicate: 'ge', actualPredicate: 'lt', field1: firstSummand, field2: secondSummand },
          },
        ],
        [
          'gt',
          {
            passes: targetVal > sum,
            reverse: 'lt',
            error: { requiredPredicate: 'gt', actualPredicate: 'le', field1: firstSummand, field2: secondSummand },
          },
        ],
      ]);

      if (predicates.has(predicate) && predicates.get(predicate).passes) {
        return null;
      }

      const targetResult = predicates.get(predicate);
      const summandResult1 = predicates.get(targetResult.reverse);
      const summandResult2 = predicates.get(targetResult.reverse);
      const targetErr: CompareToSumTargetFieldError = { [ERROR_KEY_TARGET]: targetResult.error };
      const summandErr1: CompareToSumSummandFieldError = { [ERROR_KEY_SUMMAND]: summandResult1.error };
      const summandErr2: CompareToSumSummandFieldError = { [ERROR_KEY_SUMMAND]: summandResult2.error };

      targetField.setErrors(targetErr);
      summandField1.setErrors(summandErr1);
      summandField2.setErrors(summandErr2);

      targetField.markAsTouched();
      summandField1.markAsTouched();
      summandField2.markAsTouched();

      return {
        compareToSum: {
          target: targetResult.error,
          firstSummand: summandResult1.error,
          secondSummand: summandResult2.error,
        },
      };
    };
  }

  // @TODO WAI-682: refactor into "complexity" validator

  public static url(control: AbstractControl): Record<'url', boolean> | null {
    if (!control.value) {
      return null;
    }

    // List of common valid TLDs (can be extended as needed)
    const validTLDs = new Set([
      // Generic TLDs
      'com', 'org', 'net', 'edu', 'gov', 'mil', 'int',
      'info', 'biz', 'name', 'pro', 'aero', 'coop', 'museum',
      // New generic TLDs
      'io', 'ai', 'co', 'app', 'dev', 'tech', 'online', 'site', 'website',
      'store', 'shop', 'blog', 'news', 'media', 'digital', 'agency',
      'cloud', 'email', 'work', 'studio', 'design', 'solutions',
      // Country code TLDs (common ones)
      'us', 'uk', 'ca', 'au', 'de', 'fr', 'it', 'es', 'nl', 'be', 'ch',
      'at', 'se', 'no', 'dk', 'fi', 'pl', 'cz', 'ru', 'jp', 'cn', 'in',
      'br', 'mx', 'ar', 'cl', 'nz', 'sg', 'hk', 'tw', 'kr', 'th', 'my',
      'id', 'ph', 'vn', 'za', 'eg', 'ng', 'ke', 'ae', 'sa', 'il', 'tr',
      'gr', 'pt', 'ie', 'ro', 'hu', 'bg', 'hr', 'si', 'sk', 'lt', 'lv',
      'ee', 'is', 'lu', 'mt'
    ]);

    let isValid = true;
    try {
      const url = new URL(control.value);

      // Must be http or https
      if (!['http:', 'https:'].includes(url.protocol)) {
        return { url: true };
      }

      // Validate hostname structure
      const hostname = url.hostname.toLowerCase();

      // Hostname must contain at least one dot (e.g., example.com)
      if (!hostname.includes('.')) {
        return { url: true };
      }

      // Extract TLD (everything after the last dot)
      const parts = hostname.split('.');
      const tld = parts[parts.length - 1];

      // TLD must be in the valid list
      if (!validTLDs.has(tld)) {
        return { url: true };
      }

      // Domain name (before TLD) must be at least 1 character and valid
      const domain = parts[parts.length - 2];
      if (!domain || domain.length < 1 || !/^[a-z0-9-]+$/.test(domain)) {
        return { url: true };
      }

      // Check for spaces in the original value
      if (/\s/.test(control.value)) {
        return { url: true };
      }

      isValid = true;
    } catch {
      isValid = false;
    }

    return isValid ? null : { url: true };
  }

  public static onlyDigits(control: AbstractControl): Record<'onlyDigits', boolean> | null {
    if (!control.value) {
      return null;
    }

    return /^\d+$/.test(control.value) ? { onlyDigits: true } : null;
  }

  /**
   * Password complexity validator
   *
   * Provides a clear, user‑friendly message instead of a generic pattern error.
   * Currently enforces that the password is not made of digits only.
   */
  public static passwordComplexity(control: AbstractControl): ValidationErrors | null {
    if (!control.value) {
      return null;
    }

    const value = String(control.value);

    // Reject passwords that consist only of digits (e.g. 123456789)
    if (/^\d+$/.test(value)) {
      return {
        password: {
          message: 'password must contain a combination of letters and numbers.',
        },
      };
    }

    return null;
  }

  /**
   * Integer validator
   */
  public static integer(control: AbstractControl): Record<'integer', boolean> | null {
    if (!control.value) {
      return null;
    }

    if (/^\d+$/.test(control.value)) {
      return null;
    }

    return { integer: true };
  }

  /**
   * Legal Entity Identifier validator
   *
   * The technical specification for LEI is ISO 17442. An LEI consists
   * of a 20-character alphanumeric string, with the first four characters
   * identifying the local operating unit (LOU) that issued the LEI. Characters
   * 5-18 are the unique alphanumeric string assigned to the organisation
   * by the LOU. The final two characters are checksum digits.
   */
  public static lei(control: AbstractControl): Record<'lei', boolean> | null {
    if (!control.value) {
      return null;
    }

    if (/^[A-Za-z0-9]{5,20}$/.test(control.value)) {
      return null;
    }

    return { lei: true };
  }

  /**
   * VAT identificaton number validator
   *
   * Note: Currently it supports VAT identification number validation only for Germany
   *
   * @see https://en.wikipedia.org/wiki/VAT_identification_number
   */
  public static vatin(control: AbstractControl): Record<'vatin', boolean> | null {
    if (!control.value) {
      return null;
    }

    if (/^DE\d{9}$/.test(control.value)) {
      return null;
    }

    return { vatin: true };
  }

  /**
   * Email validator with less strict rules
   *
   * Validators.email allows for (correct!) addresses, like 'john.wick@movie'.
   * We seem to not allow these in the app, hence the validator.
   */
  public static email(control: AbstractControl): Record<'email', boolean> | null {
    // these are based on the BE but not 100% accurate as there is some magic going on I couldn't decipher
    const userRegex = /^([-!#$%&\'*+/=?^_`{}|~0-9A-Za-z]+)(\.[-!#$%&\'*+/=?^_`{}|~0-9A-Za-z]+)*/;
    const domainRegex = /^((?:[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?\.)+)(?:[a-zA-Z0-9-]{2,63}(?!-))/;

    if (!control.value) {
      return null;
    }

    const parts = (control.value as string).split('@');

    if (parts.length !== 2) {
      return { email: true };
    }

    const [userPart, domainPart] = parts;

    if (userRegex.test(userPart) && domainRegex.test(domainPart)) {
      return null;
    }

    return { email: true };
  }

  /**
   * Empty value validator
   *
   * Validates if control has only whitespace chars as a value.
   */
  public static nonEmpty(control: AbstractControl): Record<'nonEmpty', boolean> | null {
    if (!control.value) {
      return null;
    }

    if (isEmpty(control.value)) {
      return { nonEmpty: true };
    }

    return null;
  }

  /**
   * Phone number validator
   *
   * Validates if control's value is a valid phone number.
   */
  public static phone(control: AbstractControl): PhoneError | null {
    if (!control.value) {
      return null;
    }

    try {
      const phoneNumber = parsePhoneNumberWithError(control.value);

      // isValid() vs isPossible(): https://github.com/catamphetamine/libphonenumber-js#using-phone-number-validation-feature
      if (!phoneNumber.isValid()) {
        return { phone: 'NOT_A_NUMBER' };
      }
    } catch (error) {
      if (error instanceof ParseError) {
        return { phone: error.message as 'INVALID_COUNTRY' | 'NOT_A_NUMBER' | 'TOO_SHORT' | 'TOO_LONG' };
      } else {
        return { phone: 'OTHER' };
      }
    }

    return null;
  }

  /**
   * E.164 phone number validator
   *
   * Validates if control's value is a valid phone number in E.164 format.
   * E.164 format requires:
   * - Value starts with '+'
   * - Followed by valid country code and subscriber number
   * - Maximum 15 digits after '+'
   *
   * Example: +1234567890, +49301234567
   */
  public static e164Phone(control: AbstractControl): PhoneError | null {
    if (!control.value) {
      return null; // Allow empty values for optional fields
    }

    const phoneValue = String(control.value).trim();

    // Check if starts with '+'
    if (!phoneValue.startsWith('+')) {
      return { phone: 'MISSING_PLUS' };
    }

    try {
      const phoneNumber = parsePhoneNumberWithError(phoneValue);

      // Validate that the phone number is valid
      if (!phoneNumber.isValid()) {
        return { phone: 'NOT_A_NUMBER' };
      }

      // Verify it can be formatted as E.164 (should always work for valid numbers)
      const e164Format = phoneNumber.format('E.164');
      if (!e164Format || !e164Format.startsWith('+')) {
        return { phone: 'NOT_A_NUMBER' };
      }
    } catch (error) {
      if (error instanceof ParseError) {
        return { phone: error.message as 'INVALID_COUNTRY' | 'NOT_A_NUMBER' | 'TOO_SHORT' | 'TOO_LONG' };
      } else {
        return { phone: 'OTHER' };
      }
    }

    return null;
  }

  /**
   * Require if another is true or false
   *
   * Validates if control's value is provided when another one requires it or disallows it,
   * e.g. requiring phone number when 2FA is true, require a field be empty when another is false
   */
  public static requiredIfOtherTruthish(controlName: string, otherControlName: string): ValidatorFn {
    const ERROR_KEY = 'required';

    const getRefs = (group: FormGroup) => {
      return {
        control: group.get(controlName),
        otherControl: group.get(otherControlName),
      };
    };

    return (group: FormGroup): Record<'required', boolean> | null => {
      const { control, otherControl } = getRefs(group);

      if (control === null || otherControl === null) {
        return null;
      }

      control.setErrors(stripError(control, ERROR_KEY));

      if (Boolean(otherControl.value) === false) {
        return null;
      }

      if (control.value !== null && control.value !== undefined && String(control.value).length > 0) {
        return null;
      }

      control.setErrors({ required: true });
      control.markAsTouched();

      return { required: true };
    };
  }

  /**
   * Require if another equals to specific value
   */
  public static requiredIfOtherEqualTo(controlName: string, otherControlName: string, compare: string): ValidatorFn {
    const ERROR_KEY = 'required';

    const getRefs = (group: FormGroup) => {
      return {
        control: group.get(controlName),
        otherControl: group.get(otherControlName),
      };
    };

    return (group: FormGroup): Record<'required', boolean> | null => {
      const { control, otherControl } = getRefs(group);

      if (control === null || otherControl === null) {
        return null;
      }

      control.setErrors(stripError(control, ERROR_KEY));

      if (String(otherControl.value) !== compare) {
        return null;
      }

      if (control.value !== null && control.value !== undefined && String(control.value).length > 0) {
        return null;
      }

      control.setErrors({ required: true });
      control.markAsTouched();

      return { required: true };
    };
  }

  /**
   * Require if some other fields are non empty
   *
   * Validates if passed controls' value is provided when dependable controls are filled
   */
  public static requiredIfOtherFilled(controlName: string, otherControlNames: string[]): ValidatorFn {
    const ERROR_KEY = 'required';

    const getRefs = (group: FormGroup) => {
      return {
        control: group?.get(controlName),
        otherControls: otherControlNames?.map((name) => group?.get(name)),
      };
    };

    return (group: FormGroup): Record<'required', boolean> | null => {
      const { control, otherControls } = getRefs(group);

      if (control.value !== null && control.value !== undefined && String(control.value).length > 0) {
        return null;
      }

      control.setErrors(stripError(control, ERROR_KEY));

      if (
        otherControls.some(
          (otherControl) =>
            otherControl.value !== null && otherControl.value !== undefined && String(otherControl.value).length > 0,
        )
      ) {
        control.setErrors({ required: true });
        control.markAsTouched();

        return { required: true };
      }

      return null;
    };
  }

  public static minCurrencyValue(min: number): ValidatorFn {
    return (control: AbstractControl): Record<'minCurrencyValue', number> | null => {
      const minCheck = Validators.min(min)(control);
      if (minCheck === null) return null;
      return { minCurrencyValue: min };
    };
  }

  public static maxCurrencyValue(max: number): ValidatorFn {
    return (control: AbstractControl): Record<'maxCurrencyValue', number> | null => {
      const maxCheck = Validators.max(max)(control);
      if (maxCheck === null) return null;
      return { maxCurrencyValue: max };
    };
  }

  public static maxVisibleTextLength(maxLength: number): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      if (control.value === null || control.value === undefined) {
        return null;
      }

      const htmlValue = String(control.value);
      const plainText = htmlValue
        .replace(/<[^>]*>/g, '')
        .replace(/&nbsp;/gi, ' ')
        .trim();
      const actualLength = plainText.length;

      if (actualLength > maxLength) {
        return {
          maxlength: {
            requiredLength: maxLength,
            actualLength,
          },
        };
      }

      return null;
    };
  }

  public static maxFileSize(maxSize: number): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      const file = control.value;
      if (file && file instanceof File && file.size > maxSize) {
        return { maxFileSize: { maxSize, actualSize: file.size } };
      }
      return null;
    };
  }

  public static maxInitialInvestmentVolume(max: number): ValidatorFn {
    return (control: AbstractControl): Record<'maxInitialInvestmentVolume', number> | null => {
      if (!control.value) {
        return null;
      }

      const value = parseFloat(control.value.toString().replace(/[^\d.-]/g, ''));
      if (isNaN(value)) {
        return null;
      }

      if (value > max) {
        return { maxInitialInvestmentVolume: max };
      }

      return null;
    };
  }

  public static isDivisibleBy(min: number, divisor: number): ValidatorFn {
    return (control: AbstractControl): Record<'divisibleBy', number> | null => {
      if (!control.value) {
        return null;
      }

      const value = control.value - min;

      if (value % divisor === 0) {
        return null;
      }

      return { divisibleBy: divisor };
    };
  }

  public static investedAmount(min: number, max?: number, step?: number): ValidatorFn {
    return (control: AbstractControl) => {
      const value = control.value || 0;
      const minCheck = WaiValidators.minCurrencyValue(min)(control);
      const maxCheck = max ? WaiValidators.maxCurrencyValue(max)(control) : null;
      const stepCheck = step ? WaiValidators.isDivisibleBy(min, step)(control) : null;
      const valueIsLessThanMax = max ? value < max : true;

      if (minCheck !== null) return minCheck;
      if (maxCheck !== null) return maxCheck;
      if (stepCheck !== null && value > min && valueIsLessThanMax) return stepCheck;

      return null;
    };
  }

  static postalCode: ValidatorFn = (control: AbstractControl) => {
    if (!control.value || control.value.toString().trim().length === 0) {
      return null;
    }

    const value = control.value.toString().trim();

    // Updated regex to allow UK postcodes: letters and numbers, with optional space
    // Examples: W12 8NS, M1 1AA, B33 8TH, CR2 6XH, DN55 1PT
    if (!/^[A-Za-z0-9\s]+$/.test(value)) {
      return { postalCode: { value: value, errorKey: 'INTL_FORM_ERROR_POSTAL_CODE_INVALID_CHARACTERS' } };
    }

    if (value.length < 3) {
      return { postalCode: { value: value, errorKey: 'INTL_FORM_ERROR_POSTAL_CODE_MIN_LENGTH' } };
    }

    if (value.length > 8) {
      return { postalCode: { value: value, errorKey: 'INTL_FORM_ERROR_POSTAL_CODE_MAX_LENGTH' } };
    }

    return null;
  };

  static beneficialOwnerPercentage: ValidatorFn = (control: AbstractControl) => {
    if (!control.value) {
      return null;
    }

    const value = parseFloat(control.value);

    if (isNaN(value)) {
      return null; // Let other validators handle non-numeric values
    }

    if (value < 25) {
      return { beneficialOwnerPercentage: { requiredMin: 25, actualValue: value } };
    }

    if (value > 100) {
      return { beneficialOwnerPercentage: { requiredMax: 100, actualValue: value } };
    }

    return null;
  };

  static dateOfBirth: ValidatorFn = (control: AbstractControl) => {
    if (!control.value) {
      return null;
    }

    const selectedDate = new Date(control.value);
    const today = new Date();

    // Reset time to compare only dates
    today.setHours(0, 0, 0, 0);
    selectedDate.setHours(0, 0, 0, 0);

    // Check if date is in the future
    if (selectedDate > today) {
      return { dateOfBirth: { errorType: 'futureDate' } };
    }

    // Calculate age
    let age = today.getFullYear() - selectedDate.getFullYear();
    const monthDiff = today.getMonth() - selectedDate.getMonth();

    // Adjust age if birthday hasn't occurred this year
    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < selectedDate.getDate())) {
      age--;
    }

    // Check age restrictions
    if (age < 18) {
      return { dateOfBirth: { errorType: 'tooYoung', actualAge: age, requiredMin: 18 } };
    }

    if (age > 100) {
      return { dateOfBirth: { errorType: 'tooOld', actualAge: age, requiredMax: 100 } };
    }

    return null;
  };
}

export function validateFileMediaType(file: File, mediaTypes: MediaType[]): boolean {
  let result = false;

  if (mediaTypes) {
    mediaTypes.forEach((mediaType) => {
      if (result) {
        // already true, so we don't need to test it further
        return;
      }
      result = new RegExp(mediaType).test(file?.type);
    });
  }

  return result;
}
